import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { FeaturesSpecsParams } from 'site-modules/shared/utils/features-specs/params';
import { getCoreUrl } from 'site-modules/shared/utils/core-link-constructor';
import { Link } from 'site-modules/shared/components/link/link';
import { AnimatedSticky } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { InventoryButton } from 'site-modules/shared/components/inventory-button/inventory-button';

import './more-about-mmy-link.scss';

function MoreAboutMMYLinkUI({
  vehicle,
  className = null,
  linkClassName = null,
  iconClassName = 'text-primary-darker',
  dataTrackingId = null,
  dataTrackingValue = null,
  creativeId = null,
  stickyCreativeId = null,
  buttonFragment = null,
  linkToReview = false,
  showBuyOnlineBtn = false,
  isMobile = false,
  params = null,
  visitorLocation = null,
}) {
  const {
    make: { slug: makeSlug },
    model: { name: modelName, slug: modelSlug },
    year,
  } = vehicle;

  const linkUrl = getCoreUrl({ makeSlug, modelSlug, year }, linkToReview);
  const hasButtons = !!(showBuyOnlineBtn || buttonFragment);

  const getLinkFragment = useCallback(
    isSticky => (
      <Link
        className={classnames('link d-inline-block', { 'py-1': isSticky }, linkClassName)}
        to={linkUrl}
        data-tracking-id={dataTrackingId}
        data-tracking-value={dataTrackingValue}
        data-test="more-about-mmy-link"
      >
        More about the {year} {modelName}
        <i className={classnames('d-inline-block ms-0_25 icon-arrow-right4 size-10', iconClassName)} aria-hidden />
      </Link>
    ),
    [dataTrackingId, dataTrackingValue, iconClassName, linkClassName, linkUrl, modelName, year]
  );

  const stickyFragment = (
    <Fragment>
      {getLinkFragment(true)}
      {hasButtons && (
        <div className="button-container">
          {showBuyOnlineBtn && (
            <Fragment>
              {!isMobile && (
                <InventoryButton params={params} makeModelSubmodelYear={vehicle} visitorLocation={visitorLocation} />
              )}
              <InventoryButton
                params={params}
                makeModelSubmodelYear={vehicle}
                visitorLocation={visitorLocation}
                isBuyOnline
              />
            </Fragment>
          )}
          {buttonFragment}
        </div>
      )}
    </Fragment>
  );

  return (
    <AnimatedSticky
      identifierClass="more-about-mmy-link"
      className={classnames('sticky-mmy-link', className)}
      stickyClassName="d-flex align-items-center justify-content-between"
      stickyFragment={stickyFragment}
      creativeId={creativeId}
      stickyCreativeId={stickyCreativeId}
    >
      {getLinkFragment(false)}
    </AnimatedSticky>
  );
}

MoreAboutMMYLinkUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  className: PropTypes.string,
  linkClassName: PropTypes.string,
  iconClassName: PropTypes.string,
  dataTrackingId: PropTypes.string,
  dataTrackingValue: PropTypes.string,
  creativeId: PropTypes.string,
  stickyCreativeId: PropTypes.string,
  buttonFragment: PropTypes.node,
  linkToReview: PropTypes.bool,
  showBuyOnlineBtn: PropTypes.bool,
  isMobile: PropTypes.bool,
  params: FeaturesSpecsParams,
  visitorLocation: VisitorEntities.Location,
};

export const MoreAboutMMYLink = connectToModel(MoreAboutMMYLinkUI, {
  visitorLocation: bindToPath('location', VisitorModel),
});
